// WHAT: Modal component for displaying active tracking session when proximity is detected
// WHY:  Provides visual feedback when user returns to computer during active tracking
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: Dialog from headlessui/react
// RISKS:
//   - NOTE: Timer updates every second - may cause performance issues
//   - NOTE: Modal closes automatically when proximity lost - user may miss information

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { type FC, useEffect, useState } from "react";

/**
 * Tracking session interface
 */
interface TrackingSession {
  /** Aspect name */
  aspect: string;
  /** Session identifier */
  id: string;
  /** Session start time */
  start_time: string;
  /** Workspace name */
  workspace: string;
}

/**
 * Props for ProximityModal component
 */
interface ProximityModalProperties {
  /** Handler for closing modal */
  onClose: () => void;
  /** Whether modal is open */
  open: boolean;
  /** Active tracking session */
  session: null | TrackingSession;
}

/**
 * WHAT:
 *   Modal component that displays active tracking session information when
 *   proximity is detected (user returns to computer).
 *
 * WHY:
 *   Provides immediate visual feedback about active tracking session and
 *   elapsed time when user returns.
 *
 * PROPS:
 *   - open: boolean — Modal visibility
 *   - onClose: () => void — Close handler
 *   - session: TrackingSession | null — Active session data
 *
 * BEHAVIOR:
 *   - Displays workspace and aspect information
 *   - Shows real-time elapsed time (updates every second)
 *   - Auto-closes when proximity is lost
 *   - Uses TracQ branding and styling
 *
 * RISKS:
 *   - NOTE: Timer updates every second - may cause performance issues
 *   - NOTE: Modal closes automatically when proximity lost - user may miss information
 *
 * @param root0
 *
 * @param root0.open
 *
 * @returns {JSX.Element | null} The proximity modal component or null if not open
 *
 * @param root0.onClose
 *
 * @param root0.session
 *
 */
const ProximityModal: FC<ProximityModalProperties> = ({ onClose, open, session }) => {
  const [elapsedTime, setElapsedTime] = useState<string>("00:00:00");

  // NOTE: Real-time timer for tracking duration
  useEffect(() => {
    if (!open || !session) {
      setElapsedTime("00:00:00");
      return;
    }

    const startTime = new Date(session.start_time).getTime();

    /**
     * WHAT:
     *   Updates elapsed time display.
     *
     * WHY:
     *   Provides real-time feedback on tracking duration.
     *
     * BEHAVIOR:
     *   - Calculates elapsed seconds from start time
     *   - Formats as HH:MM:SS
     *   - Updates state
     */
    const updateTimer = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000); // Seconds

      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor((elapsed % 3600) / 60);
      const seconds = elapsed % 60;

      const formatted = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      setElapsedTime(formatted);
    };

    // NOTE: Update immediately
    updateTimer();

    // NOTE: Then update every second
    const interval = setInterval(updateTimer, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [open, session]);

  if (!session) {
    return null;
  }

  return (
    <Dialog className="relative z-50" onClose={onClose} open={open} static>
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-slate-800/95 border border-slate-600/50 text-white rounded-2xl p-8 shadow-tracq-card w-full max-w-lg transform transition-all duration-300 scale-100">
          {/* Header */}
          <div className="text-center mb-8">
            <DialogTitle className="text-3xl font-bold text-tracq-bright-blue mb-2">
              Proximity erkannt
            </DialogTitle>
            <p className="text-slate-400 text-sm">Aktive Tracking-Session</p>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Workspace & Aspect Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              {/* Workspace Card */}
              <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-4">
                <div className="text-slate-400 text-xs font-medium mb-1 uppercase tracking-wide">
                  Workspace
                </div>
                <div className="text-white text-lg font-semibold">{session.workspace}</div>
              </div>

              {/* Aspect Card */}
              <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-4">
                <div className="text-slate-400 text-xs font-medium mb-1 uppercase tracking-wide">
                  Aspect
                </div>
                <div className="text-white text-lg font-semibold">{session.aspect}</div>
              </div>
            </div>

            {/* Timer Card - Prominent */}
            <div className="bg-gradient-to-br from-tracq-bright-blue/20 to-tracq-deep-blue/20 border-2 border-tracq-bright-blue/30 rounded-xl p-6">
              <div className="text-center">
                <div className="text-slate-400 text-xs font-medium mb-2 uppercase tracking-wide">
                  Aktive Zeit
                </div>
                <div className="text-tracq-bright-blue text-4xl font-bold font-mono tracking-wider">
                  {elapsedTime}
                </div>
                <div className="text-slate-500 text-xs mt-2">hh:mm:ss</div>
              </div>
            </div>

            {/* Info Text */}
            <div className="text-center">
              <p className="text-slate-400 text-sm">
                Das Modal schließt sich automatisch, wenn Proximity verloren geht.
              </p>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export { ProximityModal };
